import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCheck, FiEdit3, FiMoon, FiSave, FiSearch, FiSun, FiTrash2, FiX, FiCalendar, FiEye } from 'react-icons/fi';
import { useNdTheme } from '../utils/ndTheme';
import '../styles/newDesign.css';

const PRIORITIES = {
  high: { label: 'Высокий', cls: 'nd-high', color: 'var(--nd-red)', weight: 0 },
  medium: { label: 'Средний', cls: 'nd-mid', color: 'var(--nd-amber)', weight: 1 },
  low: { label: 'Низкий', cls: 'nd-low', color: 'var(--nd-green)', weight: 2 },
};

const CONFETTI_COLORS = ['#2dd4bf', '#38bdf8', '#a78bfa', '#fbbf24', '#f0566a'];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return '—';
  const raw = String(value).slice(0, 10);
  const parts = raw.split('-');
  if (parts.length !== 3) return raw;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function burstConfetti(x, y) {
  for (let i = 0; i < 16; i += 1) {
    const p = document.createElement('i');
    p.className = 'nd-cf';
    const angle = Math.random() * Math.PI * 2;
    const velocity = 40 + Math.random() * 80;
    p.style.left = `${x}px`;
    p.style.top = `${y}px`;
    p.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    document.body.appendChild(p);

    if (p.animate) {
      const anim = p.animate(
        [
          { transform: 'translate(0,0) rotate(0)', opacity: 1 },
          { transform: `translate(${Math.cos(angle) * velocity}px, ${Math.sin(angle) * velocity + 70}px) rotate(360deg)`, opacity: 0 },
        ],
        { duration: 900, easing: 'ease-out' }
      );
      anim.onfinish = () => p.remove();
    } else {
      setTimeout(() => p.remove(), 900);
    }
  }
}

/** Число, которое «подпрыгивает» при изменении — как в макете. */
function BumpNumber({ value }) {
  const [bump, setBump] = useState(false);
  const prev = useRef(value);

  useEffect(() => {
    if (prev.current === value) return undefined;
    prev.current = value;
    setBump(false);
    const id = requestAnimationFrame(() => setBump(true));
    const timer = setTimeout(() => setBump(false), 420);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(timer);
    };
  }, [value]);

  return <b className={bump ? 'nd-bump' : undefined}>{value}</b>;
}

export default function TodoBoard({ onClose, isMobile = false }) {
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [todos, setTodos] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newDate, setNewDate] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [viewTodoId, setViewTodoId] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all'); // all | open | done
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [removingIds, setRemovingIds] = useState([]);
  const [toasts, setToasts] = useState([]);
  const toastSeq = useRef(0);

  const pushToast = useCallback((text, tone) => {
    const id = ++toastSeq.current;
    setToasts(prev => [...prev, { id, text, tone }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 2800);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('todos');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setTodos(parsed);
      } catch (e) {
        console.error('Error parsing todos from localStorage:', e);
        setTodos([]);
      }
    }
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('todos', JSON.stringify(todos));
    }
  }, [todos, isInitialized]);

  const addTodo = () => {
    const title = newTitle.trim();
    if (!title) {
      pushToast('⚠ Введите текст задачи', 'nd-amber');
      return;
    }

    const todo = {
      id: Date.now().toString(),
      title,
      text: newText.trim() || '',
      completed: false,
      priority: newPriority,
      dueDate: newDate || todayIso(),
      createdAt: new Date().toISOString(),
    };

    setTodos(prev => [todo, ...prev]);
    setNewTitle('');
    setNewText('');
    setNewDate('');
    pushToast('✅ Задача создана');
  };

  const toggleTodo = (id, event) => {
    const target = todos.find(t => t.id === id);
    if (!target) return;

    if (!target.completed && event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      burstConfetti(rect.left + rect.width / 2, rect.top + rect.height / 2);
      pushToast(`🎉 Выполнено: ${target.title || 'задача'}`);
    } else if (target.completed) {
      pushToast(`↩ Возвращена в работу: ${target.title || 'задача'}`, 'nd-amber');
    }

    setTodos(prev => prev.map(todo => (todo.id === id ? { ...todo, completed: !todo.completed } : todo)));
  };

  const deleteTodo = async (id) => {
    // Карточка уезжает вправо, и только потом пропадает из списка — как в макете
    setRemovingIds(prev => [...prev, id]);
    await new Promise(resolve => setTimeout(resolve, 220));
    setTodos(prev => prev.filter(todo => todo.id !== id));
    setRemovingIds(prev => prev.filter(x => x !== id));
    pushToast('🗑 Задача удалена', 'nd-red');
  };

  const clearCompleted = () => {
    const count = todos.filter(t => t.completed).length;
    if (!count) {
      pushToast('⚠ Нет выполненных задач', 'nd-amber');
      return;
    }
    setTodos(prev => prev.filter(t => !t.completed));
    pushToast(`🗑 Удалено выполненных: ${count}`, 'nd-red');
  };

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditTitle(todo.title || '');
    setEditText(todo.text || '');
  };

  const saveEdit = () => {
    if (!editTitle.trim()) return;
    setTodos(prev => prev.map(todo => (
      todo.id === editingId ? { ...todo, title: editTitle.trim(), text: editText.trim() } : todo
    )));
    setEditingId(null);
    setEditTitle('');
    setEditText('');
    pushToast('💾 Изменения сохранены');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditText('');
  };

  const stats = {
    total: todos.length,
    completed: todos.filter(t => t.completed).length,
    active: todos.filter(t => !t.completed).length,
    high: todos.filter(t => t.priority === 'high' && !t.completed).length,
  };
  const progress = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;

  const query = search.trim().toLowerCase();
  const visibleTodos = todos
    .filter(todo => {
      if (statusFilter === 'open' && todo.completed) return false;
      if (statusFilter === 'done' && !todo.completed) return false;
      if (priorityFilter !== 'all' && (todo.priority || 'medium') !== priorityFilter) return false;
      if (!query) return true;
      return `${todo.title || ''} ${todo.text || ''}`.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      const doneDiff = (a.completed ? 1 : 0) - (b.completed ? 1 : 0);
      if (doneDiff !== 0) return doneDiff;
      const wa = (PRIORITIES[a.priority] || PRIORITIES.medium).weight;
      const wb = (PRIORITIES[b.priority] || PRIORITIES.medium).weight;
      return wa - wb;
    });

  const statusChips = [
    { id: 'all', label: 'Все' },
    { id: 'open', label: 'Открытые' },
    { id: 'done', label: 'Выполненные' },
  ];

  const priorityChips = [
    { id: 'all', label: 'Все приоритеты', color: 'var(--nd-violet)' },
    { id: 'high', label: 'Высокий', color: 'var(--nd-red)' },
    { id: 'medium', label: 'Средний', color: 'var(--nd-amber)' },
    { id: 'low', label: 'Низкий', color: 'var(--nd-green)' },
  ];

  const viewTodo = viewTodoId ? todos.find(t => t.id === viewTodoId) : null;

  const toastsNode = toasts.length > 0 && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
        <div className="nd-toasts">
          {toasts.map(t => (
            <div key={t.id} className={`nd-toast${t.tone ? ` ${t.tone}` : ''}`}>{t.text}</div>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`nd-todoWrap${isMobile ? ' nd-mobile' : ''}`} data-modal="todo-board">
      <div className="nd-todoSheet">
        <div className="nd-sheetHead">
          <div className="nd-sheetIco nd-todoIco">📝</div>
          <h2>
            Список дел
            <span>личный чек-лист · только на этом компьютере · не рабочие задачи</span>
          </h2>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          {onClose && (
            <button type="button" className="nd-sheetClose" onClick={onClose} aria-label="Закрыть" title="Закрыть">
              <FiX />
            </button>
          )}
        </div>

        <div className="nd-statChips">
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-violet)' }}><i />Всего <BumpNumber value={stats.total} /></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-green)' }}><i />Готово <BumpNumber value={stats.completed} /></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-amber)' }}><i />В работе <BumpNumber value={stats.active} /></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-red)' }}><i />Важных <BumpNumber value={stats.high} /></span>
        </div>

        <div className="nd-todoProg"><i style={{ width: `${progress}%` }} /></div>

        <div className="nd-todoForm nd-glass">
          <div className="nd-todoFRow">
            <input
              type="text"
              placeholder="Новое дело… например: Купить бумагу"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addTodo(); }}
            />
            <select value={newPriority} onChange={e => setNewPriority(e.target.value)} title="Приоритет">
              <option value="high">🔴 Высокий</option>
              <option value="medium">🟡 Средний</option>
              <option value="low">🟢 Низкий</option>
            </select>
            <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} title="Срок" />
            <button type="button" className="nd-todoAdd" onClick={addTodo}>＋ Добавить</button>
            <textarea
              placeholder="Заметка (необязательно)…"
              value={newText}
              onChange={e => setNewText(e.target.value)}
            />
          </div>
        </div>

        <div className="nd-todoTools">
          <div className="nd-todoSearch">
            <span className="nd-sIco"><FiSearch /></span>
            <input
              type="text"
              placeholder="Найти в списке дел…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {statusChips.map(chip => (
            <button
              key={chip.id}
              type="button"
              className={`nd-chip${statusFilter === chip.id ? ' nd-on' : ''}`}
              style={{ '--cc': 'var(--nd-violet)' }}
              onClick={() => setStatusFilter(chip.id)}
            >
              {chip.label}
            </button>
          ))}

          <span className="nd-sepV" />

          {priorityChips.map(chip => (
            <button
              key={chip.id}
              type="button"
              className={`nd-chip${priorityFilter === chip.id ? ' nd-on' : ''}`}
              style={{ '--cc': chip.color }}
              onClick={() => setPriorityFilter(chip.id)}
            >
              {chip.label}
            </button>
          ))}

          <span className="nd-sepV" />

          <button type="button" className="nd-chip nd-danger" onClick={clearCompleted}>
            <FiTrash2 size={12} /> Очистить готовые
          </button>
        </div>

        <div className="nd-todoList">
          {visibleTodos.length === 0 ? (
            <div className="nd-todoEmpty">
              <span>📝</span>
              {todos.length === 0 ? 'Список пуст — добавьте первое дело выше' : 'Ничего не найдено по фильтру'}
            </div>
          ) : (
            visibleTodos.map((todo, i) => {
              const priority = PRIORITIES[todo.priority] || PRIORITIES.medium;
              const editing = editingId === todo.id;

              return (
                <div
                  key={todo.id}
                  className={`nd-todoRow nd-glass${todo.completed ? ' nd-done' : ''}${removingIds.includes(todo.id) ? ' nd-out' : ''}`}
                  style={{ '--pc': priority.color, animationDelay: `${Math.min(i * 50, 400)}ms` }}
                >
                  <button
                    type="button"
                    className="nd-todoChk"
                    onClick={e => toggleTodo(todo.id, e)}
                    title="Отметить выполнение"
                  >
                    <FiCheck />
                  </button>

                  {editing ? (
                    <div className="nd-todoMain" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input
                        className="nd-todoEdit"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        placeholder="Название…"
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                      />
                      <textarea
                        className="nd-todoEdit"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        placeholder="Заметка…"
                        rows={3}
                      />
                    </div>
                  ) : (
                    <div className="nd-todoMain">
                      <b>{todo.title || todo.text || 'Без названия'}</b>
                      <span className="nd-todoMeta">
                        <span><FiCalendar size={11} /> {formatDate(todo.dueDate || todo.createdAt)}</span>
                        <span>{priority.label}</span>
                      </span>
                    </div>
                  )}

                  {!editing && <span className={`nd-todoPri ${priority.cls}`}>{priority.label}</span>}

                  {editing ? (
                    <>
                      <button type="button" className="nd-todoAct" onClick={saveEdit} title="Сохранить"><FiSave /></button>
                      <button type="button" className="nd-todoAct" onClick={cancelEdit} title="Отмена"><FiX /></button>
                    </>
                  ) : (
                    <>
                      {(todo.text || todo.title) && (
                        <button type="button" className="nd-todoAct" onClick={() => setViewTodoId(todo.id)} title="Открыть">
                          <FiEye />
                        </button>
                      )}
                      <button type="button" className="nd-todoAct" onClick={() => startEdit(todo)} title="Редактировать">
                        <FiEdit3 />
                      </button>
                      <button type="button" className="nd-todoAct nd-del" onClick={() => deleteTodo(todo.id)} title="Удалить">
                        <FiTrash2 />
                      </button>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {viewTodo && (
        <div className="nd-todoView" onClick={() => setViewTodoId(null)}>
          <div className="nd-todoViewBox" onClick={e => e.stopPropagation()}>
            <h3>
              {viewTodo.title || 'Без названия'}
              <button type="button" onClick={() => setViewTodoId(null)} aria-label="Закрыть"><FiX /></button>
            </h3>
            <div className="nd-todoViewMeta">
              <span className={`nd-todoPri ${(PRIORITIES[viewTodo.priority] || PRIORITIES.medium).cls}`}>
                {(PRIORITIES[viewTodo.priority] || PRIORITIES.medium).label}
              </span>
              <span className="nd-todoDate">{formatDate(viewTodo.dueDate || viewTodo.createdAt)}</span>
            </div>
            <div className={`nd-todoViewText${viewTodo.text ? '' : ' nd-none'}`}>
              {viewTodo.text || 'Заметки нет'}
            </div>
          </div>
        </div>
      )}

      {toastsNode}
    </div>
  );
}
